/**
 * The landing page's sound: one music bed and the odd distant gun.
 *
 * Contract (the page imports this; `hero-scene.ts` cues the guns):
 *  - `enabled` is the visitor's choice, remembered in `localStorage` under
 *    `landing.sound`; it starts true for a first visit;
 *  - nothing plays until the browser has seen a gesture: `armOnGesture()` is
 *    called by the page on mount and starts the music on the first
 *    pointerdown / keydown / touchstart if `enabled` is still true;
 *  - `toggle()` flips `enabled`, fading the music in or out;
 *  - `cue('gun-far')` plays a distant gun at low gain, only while enabled and
 *    only after the gesture, so a salvo on the hero is heard as well as seen;
 *  - `dispose()` stops everything and drops the listeners.
 *
 * Plain `HTMLAudioElement`s throughout — no Web Audio graph, no `gameAudio`:
 * this is two sounds on a page, not the battle's mixer.
 */
import { ref, watch, type Ref } from 'vue';

import { prefStorage } from '@/consent/pref-storage';
import { audioMuted, masterVolume, musicVolume } from '@/state/settings';

export type LandingCue = 'gun-far';

export interface LandingSound {
  readonly enabled: Ref<boolean>;
  armOnGesture(): void;
  toggle(): void;
  cue(name: LandingCue): void;
  dispose(): void;
}

/** A "preferences" consent item (consent/pref-storage.ts). */
const STORAGE_KEY = 'landing.sound';
const MUSIC_FADE_IN_S = 2;
const MUSIC_FADE_OUT_S = 1;
const FADE_STEP_MS = 50;
const CUE_MIN_INTERVAL_S = 0.3;
const CUE_VOLUME = 0.35;
const GUN_VARIANTS = 3;

/** `'off'` is the only value that turns it off; anything else, including a first visit with nothing stored, is on. */
function loadEnabled(): boolean {
  return prefStorage.get(STORAGE_KEY) !== 'off';
}

const enabled = ref(loadEnabled());

/** What the music bed should be heard at right now — 0 while muted, the chosen levels otherwise. */
function targetMusicVolume(): number {
  return 0.5 * musicVolume.value * (audioMuted.value ? 0 : masterVolume.value);
}

class LandingSoundImpl implements LandingSound {
  readonly enabled = enabled;

  private readonly music = new Audio('/audio/music-harbour.mp3');
  private gestureArmed = false;
  private gestureSeen = false;
  private fadeTimer: ReturnType<typeof setInterval> | null = null;
  private fadeTarget = 0;
  private lastCueAt = -Infinity;

  constructor() {
    this.music.loop = true;
    this.music.preload = 'auto';
    this.music.volume = 0;

    // A volume slider dragged mid-song, or the mute toggle, glides the music
    // to its new level rather than snapping it — only while it is actually
    // playing; arming it early would start the music before the gesture.
    watch([musicVolume, masterVolume, audioMuted], () => {
      if (this.music.paused) return;
      this.rampTo(targetMusicVolume(), MUSIC_FADE_IN_S);
    });

    document.addEventListener('visibilitychange', this.onVisibility);
  }

  private readonly onVisibility = (): void => {
    if (document.hidden) {
      this.music.pause();
    } else if (this.gestureSeen && this.enabled.value) {
      this.music.play().catch(() => {
        // Autoplay can still be refused on return to the tab; the visitor's
        // next gesture or the sound toggle will try again.
      });
    }
  };

  private readonly onGesture = (): void => {
    this.gestureSeen = true;
    if (this.enabled.value) this.startMusic();
  };

  armOnGesture(): void {
    // Back from the enlistment form: the browser already counts the earlier
    // gesture, so the music may simply resume.
    if (this.gestureSeen && this.enabled.value) this.startMusic();
    if (this.gestureArmed) return;
    this.gestureArmed = true;
    document.addEventListener('pointerdown', this.onGesture, { once: true, passive: true });
    document.addEventListener('keydown', this.onGesture, { once: true, passive: true });
    document.addEventListener('touchstart', this.onGesture, { once: true, passive: true });
  }

  private startMusic(): void {
    this.music.volume = 0;
    this.music.play().catch(() => {
      // A gesture that the browser still does not count as one; nothing to
      // do but wait for a later one.
    });
    this.rampTo(targetMusicVolume(), MUSIC_FADE_IN_S);
  }

  /** Fade the music's volume toward `target` over `seconds`; replaces any fade already running, never a hard cut. */
  private rampTo(target: number, seconds: number): void {
    if (this.fadeTimer) clearInterval(this.fadeTimer);
    this.fadeTarget = target;
    const steps = Math.max(1, Math.round((seconds * 1000) / FADE_STEP_MS));
    const start = this.music.volume;
    let step = 0;
    this.fadeTimer = setInterval(() => {
      step++;
      const t = Math.min(1, step / steps);
      this.music.volume = start + (this.fadeTarget - start) * t;
      if (t >= 1) {
        if (this.fadeTimer) clearInterval(this.fadeTimer);
        this.fadeTimer = null;
        if (this.music.volume <= 0.001) this.music.pause();
      }
    }, FADE_STEP_MS);
  }

  toggle(): void {
    enabled.value = !enabled.value;
    prefStorage.set(STORAGE_KEY, enabled.value ? 'on' : 'off');
    if (enabled.value) {
      if (this.gestureSeen) this.startMusic();
    } else {
      this.rampTo(0, MUSIC_FADE_OUT_S);
    }
  }

  cue(name: LandingCue): void {
    if (name !== 'gun-far' || !this.gestureSeen || !this.enabled.value) return;
    const now = Date.now() / 1000;
    if (now - this.lastCueAt < CUE_MIN_INTERVAL_S) return;
    this.lastCueAt = now;
    const variant = 1 + Math.floor(Math.random() * GUN_VARIANTS);
    const shot = new Audio(`/audio/gun-far-${variant}.mp3`);
    shot.volume = audioMuted.value ? 0 : CUE_VOLUME * masterVolume.value;
    shot.play().catch(() => {
      // Fire and forget: a refused one-shot is simply not heard.
    });
  }

  /**
   * The page has gone (the form opened over it). Stops the sound and drops
   * the gesture listeners, but keeps the singleton usable: the landing comes
   * back when the visitor steps back from the form, and `armOnGesture()`
   * then picks the music up again.
   */
  dispose(): void {
    if (this.fadeTimer) clearInterval(this.fadeTimer);
    this.fadeTimer = null;
    this.music.pause();
    this.music.volume = 0;
    this.gestureArmed = false;
    document.removeEventListener('pointerdown', this.onGesture);
    document.removeEventListener('keydown', this.onGesture);
    document.removeEventListener('touchstart', this.onGesture);
  }
}

export const landingSound: LandingSound = new LandingSoundImpl();
